import { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import PlanPage from '@/pages/trip/PlanPage';
import AuthPage from '@/pages/trip/AuthPage';
import { RequireAuth } from '@/app/RequireAuth';
import { ErrorBoundary } from '@/components/error/ErrorBoundary';
import { tripBasename } from '@/lib/env';

// Code-Splitting: PlanPage und AuthPage sind die zwei initial geladenen
// Routen (Plan-Hub für eingeloggte User, Auth-Form für den Rest). Alle
// anderen Pages werden per `React.lazy()` on-demand nachgeladen — der
// initiale Bundle-Hit ist dadurch deutlich kleiner.
const ChatPage = lazy(() => import('@/pages/trip/ChatPage'));
const MapPage = lazy(() => import('@/pages/trip/MapPage'));
const NowPage = lazy(() => import('@/pages/trip/NowPage'));
const CrewPage = lazy(() => import('@/pages/trip/CrewPage'));
const RecapPage = lazy(() => import('@/pages/trip/RecapPage'));
const BookletPage = lazy(() => import('@/pages/trip/BookletPage'));
const GamesPage = lazy(() => import('@/pages/trip/GamesPage'));

function PageFallback() {
  return <div className="loading">Lade …</div>;
}

function Lazy({ label, children }: { label: string; children: React.ReactNode }) {
  // ErrorBoundary außen, Suspense innen: ein Lade-Fehler des Chunks
  // (z.B. nach Deploy-invalidiertem Cache) fängt der Boundary, kein
  // weißer Bildschirm.
  return (
    <ErrorBoundary label={label}>
      <Suspense fallback={<PageFallback />}>{children}</Suspense>
    </ErrorBoundary>
  );
}

// React Router läuft mit `basename = /trips/<slug>`.
//
// Auth-Strategie:
//   - /auth ist öffentlich (LoginForm + RegisterForm, optional ?invite=<token>)
//   - Alles andere ist in <RequireAuth> gewrappt: ohne Session → Redirect /auth.
export function TripRouterProvider({ slug }: { slug: string }) {
  const protectedPlan = (
    <RequireAuth slug={slug}>
      <PlanPage slug={slug} />
    </RequireAuth>
  );

  const router = createBrowserRouter(
    [
      { path: '/auth', element: <AuthPage slug={slug} /> },
      { path: '/', element: protectedPlan },
      { path: '/plan', element: protectedPlan },
      {
        path: '/map',
        element: <RequireAuth slug={slug}><Lazy label="Karte"><MapPage slug={slug} /></Lazy></RequireAuth>,
      },
      {
        path: '/chat',
        element: <RequireAuth slug={slug}><Lazy label="Chat"><ChatPage slug={slug} /></Lazy></RequireAuth>,
      },
      {
        path: '/now',
        element: <RequireAuth slug={slug}><Lazy label="Now"><NowPage slug={slug} /></Lazy></RequireAuth>,
      },
      {
        path: '/crew/*',
        element: <RequireAuth slug={slug}><Lazy label="Crew"><CrewPage slug={slug} /></Lazy></RequireAuth>,
      },
      {
        path: '/games',
        element: <RequireAuth slug={slug}><Lazy label="Games"><GamesPage slug={slug} /></Lazy></RequireAuth>,
      },
      // Booklet vor dem Recap-Hub-Catch-all, damit der spezifische Pfad gewinnt.
      {
        path: '/recap/booklet',
        element: <RequireAuth slug={slug}><Lazy label="Booklet"><BookletPage slug={slug} /></Lazy></RequireAuth>,
      },
      {
        path: '/recap/*',
        element: <RequireAuth slug={slug}><Lazy label="Recap"><RecapPage slug={slug} /></Lazy></RequireAuth>,
      },
      // Legacy-URLs aus der vanilla-Version → Redirect auf die neuen Hub-Pfade.
      { path: '/drinks', element: <Navigate to="/crew/drinks" replace /> },
      { path: '/expenses', element: <Navigate to="/crew/expenses" replace /> },
      { path: '/bierschiss', element: <Navigate to="/crew/bierschiss" replace /> },
      { path: '/gallery', element: <Navigate to="/recap/fotos" replace /> },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
    { basename: tripBasename(slug) },
  );
  return <RouterProvider router={router} />;
}
